// X accounts as workspaces: pure helpers (no React) shared by the portal shell.
//
// Everything the portal shows belongs to the selected X account, so every account-scoped
// query key carries its id and switching accounts refetches. Settings are per account too; a null
// account id means "the user-level defaults new accounts start from" (only used before any account
// is connected).

import { PLAN_IG_ACCOUNT_LIMIT, type ID } from "@gmx/shared";

/** Query-key segment for the user-level scope (no X account selected). */
export const USER_SCOPE = "user";

/** Explicit workspace switches leave account-specific detail URLs and unsaved editors. */
export function accountSwitchDestination(pathname: string, search = ""): string {
  if (pathname === '/settings') {
    const tab = new URLSearchParams(search).get('tab');
    return tab ? `/settings?tab=${encodeURIComponent(tab)}` : '/settings';
  }
  if (pathname.startsWith('/crm/contacts')) return '/crm/contacts';
  if (pathname.startsWith('/crm/tasks')) return '/crm/tasks';
  const root = '/' + pathname.split('/').filter(Boolean)[0];
  return ['/crm','/outreach','/engage','/autoresponder','/comments','/scheduler','/analytics','/replies','/followers','/buddies','/spotlight','/dream100','/autocaption','/autotag','/snapshots'].includes(root) ? root : pathname;
}

/** Core settings (`GET /api/settings?igAccountId=`). `["settings"]` still invalidates every scope. */
export const settingsKeys = {
  all: ["settings"] as const,
  scope: (igAccountId: ID | null) => ["settings", igAccountId ?? USER_SCOPE] as const,
};

export type FollowDecision = "none" | "follow" | "leave";

export interface FollowInput {
  /** The igAccountId of the entity a detail page loaded (undefined while it loads). */
  entityAccountId: ID | null | undefined;
  /** The selected workspace. */
  currentId: ID | null | undefined;
  /** The user's connected accounts. */
  accountIds: readonly ID[];
  /** The entity account the workspace was last in sync with on this page (null = never). */
  syncedFor: ID | null;
}

/**
 * Deep links: keep the workspace header matching the entity on screen.
 *   follow — the entity belongs to another connected account: switch the workspace to it.
 *   leave  — the page was in sync and the user then switched accounts themselves: get off the
 *            entity (close the drawer / back to the list) instead of dragging them back.
 *   none   — nothing to do (still loading, already in sync, or the account isn't connected anymore).
 */
export function followDecision({ entityAccountId, currentId, accountIds, syncedFor }: FollowInput): FollowDecision {
  if (!entityAccountId || !currentId) return "none";
  if (entityAccountId === currentId) return "none";
  if (!accountIds.includes(entityAccountId)) return "none";
  return syncedFor === entityAccountId ? "leave" : "follow";
}

/** Plans you can move up to, in order (trial and lifetime aren't sold as upgrades). */
const UPGRADE_PLANS = ["starter", "pro", "agency"] as const;

/** Plans that run more X accounts than `limit`, smallest first: [{ plan: "pro", limit: 3 }, …]. */
export function biggerPlans(limit: number, table: Record<string, number> = PLAN_IG_ACCOUNT_LIMIT): Array<{ plan: string; limit: number }> {
  return UPGRADE_PLANS.filter((p) => (table[p] ?? 0) > limit).map((p) => ({ plan: p, limit: table[p]! }));
}
